// The trajectory view (GDD §15.1-3, M5): net-worth curve so far, projected
// forward under the current plan, with faint grinder/balanced reference
// ghosts and the doubling time printed on it. "Philosophy #2: build the
// userscript natively." The G4 crossover is a visible moment on this chart.

import type { Params } from '@lifespan/engine';
import { ghostCurves } from '../game/ghosts.ts';
import { trajectory, type Session, type TrajectoryPoint } from '../game/driver.ts';

const W = 560;
const H = 170;
const PAD = 6;
const MAX_PTS = 200;

function thin<T>(xs: T[], n: number): T[] {
  if (xs.length <= n) return xs;
  const out: T[] = [];
  const step = (xs.length - 1) / (n - 1);
  for (let i = 0; i < n; i++) out.push(xs[Math.round(i * step)]!);
  return out;
}

/** symmetric-log squash so $10k and $20M share one readable axis */
const squash = (S: number) => Math.sign(S) * Math.log10(1 + Math.abs(S) / 1000);

export function TrajectoryView({ session, P }: { session: Session; P: Params }) {
  const traj = trajectory(session, P);
  const ghosts = ghostCurves(P);

  const all: TrajectoryPoint[][] = [traj.past, traj.projected, ghosts.balanced, ghosts.grinder];
  let x0 = P.startAge;
  let x1 = P.startAge + 1;
  let y0 = 0;
  let y1 = 1;
  for (const series of all) {
    for (const p of series) {
      x1 = Math.max(x1, p.age);
      y0 = Math.min(y0, squash(p.S));
      y1 = Math.max(y1, squash(p.S));
    }
  }
  const sx = (age: number) => PAD + ((age - x0) / (x1 - x0)) * (W - 2 * PAD);
  const sy = (S: number) =>
    H - PAD - ((squash(S) - y0) / Math.max(1e-9, y1 - y0)) * (H - 2 * PAD);
  const line = (pts: TrajectoryPoint[]) =>
    thin(pts, MAX_PTS)
      .map((p) => `${sx(p.age).toFixed(1)},${sy(p.S).toFixed(1)}`)
      .join(' ');

  const lastPast = traj.past[traj.past.length - 1];
  const projLine =
    lastPast && traj.projected.length > 0
      ? line([lastPast, ...traj.projected])
      : '';

  return (
    <section className="panel trajectory" aria-label="Trajectory">
      <div className="panel-title">
        Trajectory — projected under the current plan
        {traj.doublingYears !== null && (
          <span className="traj-doubling">
            {' '}
            · doubling every ~{traj.doublingYears.toFixed(1)}y
          </span>
        )}
      </div>
      <svg
        className="traj-svg"
        viewBox={`0 0 ${W} ${H}`}
        preserveAspectRatio="none"
        role="img"
        aria-label="Net worth: your curve, your projection, and the balanced and grinder reference ghosts"
      >
        <line className="traj-zero" x1={PAD} y1={sy(0)} x2={W - PAD} y2={sy(0)} />
        <polyline className="traj-ghost" points={line(ghosts.balanced)} fill="none" />
        <polyline className="traj-ghost grinder" points={line(ghosts.grinder)} fill="none" />
        {projLine && <polyline className="traj-projected" points={projLine} fill="none" />}
        <polyline className="traj-past" points={line(traj.past)} fill="none" />
        {traj.projectedDeath !== null && (
          <text className="traj-death" x={sx(traj.projectedDeath)} y={12} textAnchor="middle">
            † {traj.projectedDeath.toFixed(1)}
          </text>
        )}
      </svg>
      <div className="traj-legend">
        <span className="lg you">— you</span>
        <span className="lg proj">– – this plan continued</span>
        <span className="lg bal">— balanced ghost</span>
        <span className="lg gr">— grinder ghost</span>
      </div>
    </section>
  );
}
